import React, { Component } from 'react';
import moment from 'moment';

/* ── helpers ─────────────────────────────────────────────── */
const val = (v, fallback = '—') => (v !== undefined && v !== null && v !== '') ? v : fallback;
const arr = (a) => (Array.isArray(a) && a.length > 0) ? a : null;
const num = (v) => Number(v) || 0;
const sumColumn = (rows, key) => rows.reduce((sum, row) => sum + num(row[key]), 0);
const eventTypeLabel = (eventType, eventTypeOther) => {
	if (!eventType) return eventTypeOther || null;
	return String(eventType).toLowerCase() === 'other' ? (eventTypeOther || eventType) : eventType;
};

const toMinutes = (time) => {
	const parsed = moment(time, ['h:mm A', 'HH:mm'], true);
	if (!parsed.isValid()) return Number.MAX_SAFE_INTEGER;
	return (parsed.hours() * 60) + parsed.minutes();
};

const getDurationMinutes = (item) => {
	const start = toMinutes(item.startTime);
	const end = toMinutes(item.endTime);
	if (start === Number.MAX_SAFE_INTEGER || end === Number.MAX_SAFE_INTEGER) {
		return Number.MAX_SAFE_INTEGER;
	}
	if (end >= start) return end - start;
	return (24 * 60 - start) + end;
};

const sortAgendaItems = (items = []) => {
	return [...items].sort((a, b) => {
		const startDiff = toMinutes(a.startTime) - toMinutes(b.startTime);
		if (startDiff !== 0) return startDiff;

		const durationDiff = getDurationMinutes(a) - getDurationMinutes(b);
		if (durationDiff !== 0) return durationDiff;

		const nameA = (a.sessionName || a.roomTypeLabel || a.roomType || '').toLowerCase();
		const nameB = (b.sessionName || b.roomTypeLabel || b.roomType || '').toLowerCase();
		return nameA.localeCompare(nameB);
	});
};

const sortAgendaEntriesByDate = (groupedAgenda, baseYear) => {
	return Object.entries(groupedAgenda)
		.map(([dateKey, items]) => ([dateKey, sortAgendaItems(items)]))
		.sort(([dateA], [dateB]) => {
			const parsedA = moment(`${dateA} ${baseYear}`, 'MMM D YYYY', true);
			const parsedB = moment(`${dateB} ${baseYear}`, 'MMM D YYYY', true);

			if (!parsedA.isValid() && !parsedB.isValid()) return dateA.localeCompare(dateB);
			if (!parsedA.isValid()) return 1;
			if (!parsedB.isValid()) return -1;
			return parsedA.valueOf() - parsedB.valueOf();
		});
};

const sortByDate = (rows = []) => {
	return [...rows]
		.map((row, index) => ({ row, index }))
		.sort((a, b) => {
			const aHasDate = !!a.row?.date;
			const bHasDate = !!b.row?.date;

			if (!aHasDate && !bHasDate) return a.index - b.index;
			if (!aHasDate) return 1;
			if (!bHasDate) return -1;

			const aTime = moment(a.row.date).valueOf();
			const bTime = moment(b.row.date).valueOf();

			if (aTime === bTime) return a.index - b.index;
			return aTime - bTime;
		})
		.map(({ row }) => row);
};

const ReviewCard = ({ title, stepIndex, onGoToStep, children }) => (
	<div className="rfp-review-card">
		<div className="rfp-review-card-header">
			<h3 className="rfp-review-card-title">{title}</h3>
			{onGoToStep && (
				<button
					type="button"
					className="rfp-review-edit-btn"
					onClick={() => onGoToStep(stepIndex)}
				>
					<i className="las la-pencil-alt" />
					Edit
				</button>
			)}
		</div>
		<div className="rfp-review-card-body">{children}</div>
	</div>
);

const Field = ({ label, value }) => (
	<div className="rfp-review-field">
		<span className="rfp-review-label">{label}</span>
		<span className="rfp-review-value">{val(value)}</span>
	</div>
);

const Chips = ({ items }) => (
	<div className="rfp-review-chips">
		{(items || []).map((item, i) => (
			<span key={i} className="rfp-review-chip">{item}</span>
		))}
	</div>
);

class ReviewSubmitStep extends Component {
	render() {
		const { data, onGoToStep, teamMembers } = this.props;
		const resolvedEventType = eventTypeLabel(data.eventType, data.eventTypeOther);
		const sortedRoomBlock = sortByDate(data.roomBlock || []);

		/* ── Event Details ── */
		const eventSection = (
			<ReviewCard title="Event Details" stepIndex={0} onGoToStep={onGoToStep}>
				<Field label="Event Name" value={data.eventName} />
				<Field label="Event Type" value={resolvedEventType} />
				<Field label="Peak Attendees" value={data.expectedPeakAttendees} />
				<Field label="Max Room Rate" value={data.maxRoomRate ? `$${data.maxRoomRate}` : null} />
				<Field label="Hotel Star Rating" value={data.hotelStarRating ? `${data.hotelStarRating} ★` : null} />
				<Field label="Desired Brands" value={data.desiredBrands} />
			</ReviewCard>
		);

		/* ── Locations ── */
		const locs = data.locations || {};
		const locationSection = (
			<ReviewCard title="Locations" stepIndex={1} onGoToStep={onGoToStep}>
				{arr(locs.states) ? (
					<div className="rfp-review-row">
						<span className="rfp-review-label">States</span>
						<Chips items={locs.states} />
					</div>
				) : null}
				{arr(locs.regions) ? (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Regions</span>
						<Chips items={locs.regions} />
					</div>
				) : <Field label="Regions" value={null} />}
				{arr(locs.countries) ? (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Countries</span>
						<Chips items={locs.countries} />
					</div>
				) : null}
				{arr(locs.cities) ? (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Cities</span>
						<Chips items={locs.cities} />
					</div>
				) : null}
				{arr(locs.hotels) ? (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Hotels</span>
						<Chips items={locs.hotels} />
					</div>
				) : null}
			</ReviewCard>
		);

		/* ── Preferred Dates ── */
		const fmt = (d) => d ? moment(d).format('MMM D, YYYY') : null;
		const datesSection = (
			<ReviewCard title="Preferred Dates" stepIndex={2} onGoToStep={onGoToStep}>
				<Field label="Arrival" value={fmt(data.preferredArrivalDate)} />
				<Field label="Departure" value={fmt(data.preferredDepartureDate)} />
				{arr(data.alternativeDates) && (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Alt. Dates</span>
						<div className="rfp-review-value-col">
							{data.alternativeDates.map((d, i) => (
								<span key={i}>
									{fmt(d.arrivalDate)} – {fmt(d.departureDate)}
								</span>
							))}
						</div>
					</div>
				)}
				{arr(data.programDays) && (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Program Days</span>
						<Chips items={data.programDays} />
					</div>
				)}
				{data.openToAltArrival && arr(data.altArrivalDays) && (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Alt. Arrival Days</span>
						<Chips items={data.altArrivalDays} />
					</div>
				)}
				{arr(sortedRoomBlock) && (
					<div className="rfp-review-row" style={{ marginTop: 8 }}>
						<span className="rfp-review-label">Room Block</span>
						<div className="rfp-review-rb-table">
							<div className="rfp-review-rb-head">
								<span>Date</span><span>King</span><span>Dbl</span><span>Suite</span><span>Staff</span><span>Total</span>
							</div>
							{sortedRoomBlock.map((row, i) => (
								<div key={i} className="rfp-review-rb-row">
									<span>{fmt(row.date)}</span>
									<span>{row.king || 0}</span>
									<span>{row.double || 0}</span>
									<span>{row.suite || 0}</span>
									<span>{row.staff || 0}</span>
									<span><strong>{row.total || 0}</strong></span>
								</div>
							))}
							{(() => {
								const totKing = sumColumn(sortedRoomBlock, 'king');
								const totDouble = sumColumn(sortedRoomBlock, 'double');
								const totSuite = sumColumn(sortedRoomBlock, 'suite');
								const totStaff = sumColumn(sortedRoomBlock, 'staff');
								const totAll = totKing + totDouble + totSuite + totStaff;
								return (
									<div className="rfp-review-rb-row" style={{ borderTop: '2px solid #e5e7eb', background: '#f9fafb', fontWeight: 600 }}>
										<span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Totals</span>
										<span>{totKing}</span>
										<span>{totDouble}</span>
										<span>{totSuite}</span>
										<span>{totStaff}</span>
										<span><strong>{totAll}</strong></span>
									</div>
								);
							})()}
						</div>
					</div>
				)}
				{data.datePatternNotes && <Field label="Pattern Notes" value={data.datePatternNotes} />}
			</ReviewCard>
		);

		/* ── Meeting Space ── */
		const agendaItems = data.agendaItems || [];
		const groupedAgenda = {};
		agendaItems.forEach((item) => {
			const dates = item.selectedDates || [];
			if (dates.length === 0) {
				if (!groupedAgenda['Unassigned']) groupedAgenda['Unassigned'] = [];
				groupedAgenda['Unassigned'].push(item);
			} else {
				dates.forEach((d) => {
					if (!groupedAgenda[d]) groupedAgenda[d] = [];
					groupedAgenda[d].push(item);
				});
			}
		});
		const agendaBaseYear = data.preferredArrivalDate ? moment(data.preferredArrivalDate).year() : moment().year();
		const sortedGroupedAgendaEntries = sortAgendaEntriesByDate(groupedAgenda, agendaBaseYear);

		const agendaSection = (
			<ReviewCard title="Meeting Space &amp; Agenda" stepIndex={3} onGoToStep={onGoToStep}>
				{agendaItems.length > 0 ? (
					sortedGroupedAgendaEntries.map(([date, items]) => (
						<div key={date} style={{ marginBottom: 12 }}>
							<div className="rfp-review-date-group-label">{date}</div>
							{items.map((item, i) => (
								<div key={i} className="rfp-review-agenda-row">
									<span className="rfp-review-agenda-time">
										{item.startTime} – {item.endTime}
									</span>
									<span className="rfp-review-agenda-type">{item.roomType}</span>
									{item.attendees > 0 && (
										<span className="rfp-review-meta">
											<i className="las la-user" /> {item.attendees}
										</span>
									)}
									{arr(item.setupPreference) && (
										<span className="rfp-review-meta">
											<i className="las la-th" /> {
												item.setupPreference[0] === 'Other' && item.setupPreferenceOther
													? item.setupPreferenceOther
													: item.setupPreference[0]
											}
										</span>
									)}
									{item.sessionName && (
										<span className="rfp-review-meta rfp-review-italic">{item.sessionName}</span>
									)}
									{item.require24Hour && (
										<span className="rfp-review-badge">24hr</span>
									)}
								</div>
							))}
						</div>
					))
				) : (
					<span className="rfp-review-empty">No agenda items added.</span>
				)}
			</ReviewCard>
		);

		/* ── Concessions ── */
		const allConcessions = [
			...(data.selectedAutoItems || []),
			...(data.customConcessions || [])
		];
		const concessionsSection = (
			<ReviewCard title="Concessions" stepIndex={4} onGoToStep={onGoToStep}>
				{arr(allConcessions) ? (
					<Chips items={allConcessions} />
				) : (
					<span className="rfp-review-empty">No concessions specified.</span>
				)}
			</ReviewCard>
		);

		/* ── Key Considerations ── */
		const considerationsSection = (
			<ReviewCard title="Key Considerations" stepIndex={5} onGoToStep={onGoToStep}>
				{data.decisionFactors && <Field label="Decision Factors" value={data.decisionFactors} />}
				{data.eventHistory && <Field label="Event History" value={data.eventHistory} />}
				{data.reservationNotes && <Field label="Reservation Notes" value={data.reservationNotes} />}
				{!data.decisionFactors && !data.eventHistory && !data.reservationNotes && (
					<span className="rfp-review-empty">No considerations added.</span>
				)}
			</ReviewCard>
		);

		/* ── Additional Services ── */
		const servicesSection = (
			<ReviewCard title="Additional Services" stepIndex={6} onGoToStep={onGoToStep}>
				{arr(data.additionalServices) ? (
					<Chips items={data.additionalServices} />
				) : (
					<span className="rfp-review-empty">No additional services selected.</span>
				)}
			</ReviewCard>
		);

		/* ── Contract Signee ── */
		const signeeSection = (
			<ReviewCard title="Contract Signee" stepIndex={7} onGoToStep={onGoToStep}>
				<Field label="Name" value={[data.contractContactName].filter(Boolean).join(' ') || null} />
				<Field label="Title" value={data.contractTitle} />
				<Field label="Company" value={data.contractCompanyName} />
				<Field label="Email" value={data.contractEmail} />
				<Field label="Phone" value={data.contractPhone} />
				<Field label="Signer Location" value={data.contractSignerLocation} />
			</ReviewCard>
		);

		/* ── Team Members ── */
		const selectedMemberIds = data.notifyTeamMembers || [];
		const selectedMembers = (teamMembers || []).filter(m => selectedMemberIds.includes(m._id));
		const teamMembersSection = (
			<ReviewCard title="Team Members" stepIndex={7} onGoToStep={onGoToStep}>
				{selectedMembers.length > 0 ? (
					selectedMembers.map((m) => (
						<div key={m._id} className="rfp-review-field">
							<span className="rfp-review-label">{m.firstName} {m.lastName}</span>
							<span className="rfp-review-value">{m.emailAddress}</span>
						</div>
					))
				) : (
					<span className="rfp-review-empty">No team members selected for notification.</span>
				)}
			</ReviewCard>
		);

		return (
			<div className="rfp-step-content">
				<h1 className="rfp-step-title">Review &amp; Submit</h1>
				<p className="rfp-step-sub">
					Everything looks good? Hit <strong>Submit RFP</strong> in the header or below. 
					Click <strong>Edit</strong> on any section to make changes.
				</p>

				<div className="rfp-review-grid">
					{eventSection}
					{locationSection}
					{datesSection}
					{agendaSection}
					{concessionsSection}
					{considerationsSection}
					{servicesSection}
					{signeeSection}
					{teamMembersSection}
				</div>
			</div>
		);
	}
}

export default ReviewSubmitStep;
